import { Box, alpha } from '@mui/material';
import { type ReactNode } from 'react';

/**
 * Cool blue-grey accent for the step's primary editing group (contouring).
 * A UI-only tint (not a brand token): muted and slightly cooler than the
 * neutral wells so that group stands out by weight rather than by a loud hue,
 * and stays clear of the teal primary that signals a selected tool.
 */
const ACCENT_COLOR = '#90A4B8';

interface ToolGroupProps {
    children: ReactNode;
    /**
     * Accent the cluster to mark it as the step's primary editing tools (the
     * contouring group), setting it apart from the neutral view / navigation
     * group with a restrained cool blue-grey tint.
     */
    accent?: boolean;
}

/**
 * Wraps a related set of toolbar buttons in a subtly tinted, rounded "well"
 * so the step toolbar reads as distinct clusters (view manipulation vs.
 * editing vs. history) instead of one undifferentiated row. Buttons within a
 * group sit closer together than the gap between groups, reinforcing the
 * grouping by proximity. Replaces the old thin vertical dividers.
 *
 * The toolbar sits on a fixed dark glass overlay (theme-mode independent), so
 * the well tints are expressed relative to that dark backdrop.
 */
export default function ToolGroup({
    children,
    accent = false,
}: ToolGroupProps) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                px: 0.5,
                py: 0.25,
                borderRadius: 1,
                backgroundColor: accent
                    ? alpha(ACCENT_COLOR, 0.16)
                    : alpha('#ffffff', 0.05),
                border: '1px solid',
                borderColor: accent
                    ? alpha(ACCENT_COLOR, 0.4)
                    : alpha('#ffffff', 0.08),
            }}
        >
            {children}
        </Box>
    );
}
